import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Calendar, Car, User, Loader2, CheckCircle2, AlertCircle, FileText, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractSign() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [signed, setSigned] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedRegistry, setAcceptedRegistry] = useState(false);

  useEffect(() => {
    if (!token) { setError("Link non valido — token mancante"); setLoading(false); return; }
    axios.get(`${API}/contracts/${id}/sign/public`, { params: { token } })
      .then((r) => {
        setInfo(r.data);
        if (r.data.verified) setSigned(true);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Impossibile caricare il contratto"))
      .finally(() => setLoading(false));
  }, [id, token]);

  const sign = async (e) => {
    e.preventDefault();
    if (otp.length < 4) return toast.error("Inserisci il codice OTP a 6 cifre");
    if (!acceptedTerms) return toast.error("Devi accettare i Termini e Condizioni di noleggio");
    if (!acceptedRegistry) return toast.error("Devi acconsentire al registro condiviso di tutela del settore (Art. 19)");
    setVerifying(true);
    try {
      await axios.post(`${API}/contracts/${id}/sign/verify`, {
        otp,
        client_ip: "",  // backend non lo riempie da JS; può essere arricchito da reverse proxy
        user_agent: navigator.userAgent,
      });
      setSigned(true);
      toast.success("Contratto firmato con successo");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OTP non valido");
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] text-white">
        <Loader2 className="animate-spin text-[#ff0033]" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] text-white p-6">
        <div className="max-w-md w-full glass-strong rounded-3xl p-8 border border-[#ff0033]/40 text-center">
          <AlertCircle size={42} className="text-[#ff0033] mx-auto mb-3" />
          <div className="font-display text-xl font-bold mb-2">Accesso negato</div>
          <div className="text-sm text-white/60">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white py-10 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#ff0033]/15 border border-[#ff0033]/30 text-[#ff5577] text-[10px] uppercase tracking-widest mb-3">
            <ShieldCheck size={11} /> FabriGroup · Firma Elettronica eIDAS
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase">Firma il tuo contratto</h1>
          <p className="text-sm text-white/45 mt-2">Validazione documento conforme art. 20 CAD, Reg. UE 910/2014</p>
        </div>

        {/* Contract summary */}
        <div className="glass rounded-2xl p-6 border border-white/10 mb-6">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Contratto N° {info?.numero}</div>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3"><User size={14} className="text-[#ff5577] mt-1" />
              <div><div className="text-[10px] uppercase tracking-widest text-white/40">Conduttore</div><div className="font-medium">{info?.cliente}</div></div>
            </div>
            <div className="flex items-start gap-3"><Car size={14} className="text-[#ff5577] mt-1" />
              <div><div className="text-[10px] uppercase tracking-widest text-white/40">Veicolo</div><div className="font-medium">{info?.veicolo}</div></div>
            </div>
            <div className="flex items-start gap-3"><Calendar size={14} className="text-[#ff5577] mt-1" />
              <div><div className="text-[10px] uppercase tracking-widest text-white/40">Periodo</div><div className="font-medium">Dal {info?.data_inizio}{info?.ora_consegna ? ` h ${info.ora_consegna}` : ''} al {info?.data_fine}{info?.ora_riconsegna ? ` h ${info.ora_riconsegna}` : ''}</div></div>
            </div>
            <div className="flex items-center justify-between border-t border-white/5 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-white/40">Corrispettivo totale</div>
              <div className="font-display text-2xl font-extrabold neon-text">€ {(+info?.costo_totale || 0).toFixed(2)}</div>
            </div>
          </div>
        </div>

        {signed ? (
          <div className="glass-strong rounded-2xl p-8 border border-emerald-500/40 text-center">
            <CheckCircle2 size={56} className="text-emerald-400 mx-auto mb-4" />
            <div className="font-display text-2xl font-bold mb-2">Contratto firmato ✓</div>
            <div className="text-sm text-white/60">
              Documento sottoscritto digitalmente con firma elettronica avanzata.<br />
              Riceverai conferma via email/WhatsApp. Grazie!
            </div>
            {info?.verified_at && (
              <div className="text-xs text-white/40 mt-4 font-mono">
                Verificato: {new Date(info.verified_at).toLocaleString("it-IT")}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={sign} className="glass-strong rounded-2xl p-6 border border-[#ff0033]/30 space-y-5">
            {/* TERMINI E CONDIZIONI */}
            <div className="bg-white/[0.02] border border-white/10 rounded-xl overflow-hidden">
              <button type="button" onClick={() => setShowTerms(!showTerms)}
                data-testid="toggle-terms"
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.04] transition">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/70">
                  <FileText size={14} className="text-[#ff5577]" />
                  Termini e Condizioni di Noleggio
                </div>
                {showTerms ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showTerms && (
                <div className="px-4 py-3 border-t border-white/5 max-h-72 overflow-y-auto text-[11px] leading-relaxed text-white/65 space-y-2" data-testid="terms-content">
                  <p><b className="text-white/90">Art. 2 — Durata e termine essenziale.</b> La riconsegna oltre 24h dal termine costituisce inadempimento grave ex art. 1457 c.c. con applicazione di penale giornaliera.</p>
                  <p><b className="text-white/90">Art. 4 — Divieti d'uso.</b> Vietato condurre in stato di ebbrezza (art. 186 CdS), sotto sostanze stupefacenti (art. 187 CdS), gare, subnoleggio, espatrio senza autorizzazione scritta.</p>
                  <p><b className="text-white/90">Art. 5 — Franchigie e cauzione.</b> Cauzione trattenuta in caso di danni non coperti, sinistri con responsabilità, multe o pulizia straordinaria.</p>
                  <p><b className="text-white/90">Art. 8 — Multe e infrazioni.</b> Tutte le sanzioni notificate al Locatore saranno riaddebitate al Conduttore con maggiorazione spese di gestione (€ 30 cad).</p>
                  <p><b className="text-white/90">Art. 11 — Pagamenti e mora.</b> Interessi di mora ex D.Lgs. 231/2002. Compensazione del deposito cauzionale ex art. 1241 c.c.</p>
                  <p><b className="text-white/90">Art. 12 — GPS e geolocalizzazione (GDPR).</b> Veicolo dotato di tracciamento GPS per sicurezza e antifurto. Consenso ex artt. 6 e 13 GDPR.</p>
                  <p><b className="text-white/90">Art. 13 — Risoluzione di diritto.</b> Mancato pagamento, ritardata riconsegna, uso difforme: risoluzione immediata e iscrizione in blacklist ex art. 6 lett. f) GDPR.</p>
                  <p><b className="text-white/90">Art. 15 — Firma elettronica avanzata.</b> L'OTP costituisce firma elettronica avanzata ex art. 20 D.Lgs. 82/2005 e Reg. UE 910/2014 (eIDAS).</p>
                  <p><b className="text-white/90">Art. 17 — Foro competente.</b> Foro del Locatore, salvo le competenze inderogabili a tutela del consumatore.</p>
                  <p className="bg-amber-500/10 border border-amber-500/40 rounded p-2 mt-3">
                    <b className="text-amber-300">Art. 19 — Registro condiviso di tutela settore automotive (legittimo interesse — art. 6, par. 1, lett. f) GDPR).</b><br />
                    Il Cliente accetta che, in caso di gravi inadempimenti contrattuali, frodi o danni non risarciti, l'identificativo dei propri dati fiscali (CF/P.IVA) potrà essere inserito in forma <b>crittografata e pseudonimizzata</b> in un registro condiviso di tutela del settore automotive accessibile esclusivamente alle aziende di noleggio aderenti, per finalità di prevenzione frodi. Conservazione max 24 mesi dall'ultimo evento; diritti di accesso/rettifica/cancellazione/opposizione esercitabili presso la PEC del Locatore.
                  </p>
                </div>
              )}
            </div>

            {/* CHECKBOX ACCETTAZIONE */}
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg bg-white/[0.02] border border-white/10 hover:bg-white/[0.04]">
                <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)}
                  data-testid="checkbox-terms"
                  className="mt-0.5 w-4 h-4 accent-[#ff0033]" />
                <span className="text-[11px] text-white/70 leading-relaxed">
                  Dichiaro di aver letto e accettato i <b>Termini e Condizioni di noleggio</b> e approvo specificamente, ex artt. 1341 e 1342 c.c., le clausole vessatorie indicate.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg bg-amber-500/[0.05] border border-amber-500/30 hover:bg-amber-500/[0.08]">
                <input type="checkbox" checked={acceptedRegistry} onChange={(e) => setAcceptedRegistry(e.target.checked)}
                  data-testid="checkbox-registry"
                  className="mt-0.5 w-4 h-4 accent-amber-400" />
                <span className="text-[11px] text-white/75 leading-relaxed">
                  <b className="text-amber-300">Art. 19 — Registro condiviso settore automotive.</b> Accetto che, in caso di gravi inadempimenti contrattuali, frodi o danni non risarciti, l'identificativo dei miei dati fiscali potrà essere inserito in forma crittografata in un registro condiviso di tutela del settore automotive.
                </span>
              </label>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#ff5577] mb-2">Codice OTP a 6 cifre</div>
              <input
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                placeholder="• • • • • •"
                autoFocus
                data-testid="public-otp-input"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-4 py-4 font-mono text-center text-3xl tracking-[0.6em] focus:border-[#ff0033]/60 focus:outline-none"
              />
              <div className="text-[10px] text-white/40 mt-2">
                Il codice ti è stato inviato dall'operatore via {info?.channel === "whatsapp" ? "WhatsApp" : info?.channel === "sms" ? "SMS" : "email"}. Valido 30 minuti.
              </div>
            </div>

            <button type="submit" disabled={verifying || otp.length < 4 || !acceptedTerms || !acceptedRegistry}
              data-testid="public-sign-btn"
              className="btn-neon w-full py-3.5 rounded-full text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {verifying ? <><Loader2 size={14} className="animate-spin" /> Firma in corso…</> : <><ShieldCheck size={14} /> Conferma & Firma</>}
            </button>

            <div className="text-[10px] text-white/35 text-center border-t border-white/5 pt-4 leading-relaxed">
              Inserendo l'OTP e cliccando "Conferma & Firma" approvi specificamente, ex art. 1341/1342 c.c., le clausole vessatorie indicate nel contratto e dichiari di averlo letto integralmente. La sottoscrizione mediante OTP equivale a firma autografa ai sensi dell'art. 20, comma 1-bis, D.Lgs. 82/2005 (CAD) e Reg. UE 910/2014 (eIDAS).
            </div>
          </form>
        )}

        <div className="text-center mt-8 text-[10px] uppercase tracking-widest text-white/30">
          FabriGroup Rent Manager · Firma a distanza sicura
        </div>
      </div>
    </div>
  );
}
